import React, { useEffect, useState } from 'react';
import { Button, Typography, Spin, Tabs, Modal, message } from 'antd';
import { makeStyles } from '@material-ui/core';
import PlayerTable from './PlayerTable';
import PlayerGroupModal from './PlayerGroupModal';
import ExportPlayersModal from './ExportPlayersModal';
import HoleAssignmentBoard from './HoleAssignmentBoard';
import TournamentSettingsForm from './TournamentSettingsForm';
import { adminCard, adminColors } from './adminStyles';

const { Title } = Typography;
const { TabPane } = Tabs;

const useStyles = makeStyles((theme) => ({
  // iOS Safari zooms the page into any field under 16px when it's focused.
  '@media (pointer: coarse)': {
    '@global': {
      '.ant-input, .ant-input-number-input, .ant-select-selection-search-input': {
        fontSize: 16,
      },
    },
  },
  root: {
    ...adminCard,
    borderRadius: 12,
    [theme.breakpoints.down('sm')]: {
      // The site pads every page by 30px, which phones need back.
      margin: '0 -30px',
      border: 'none',
      borderRadius: 0,
    },
  },
  header: {
    padding: '16px 24px 0',
    borderBottom: `1px solid ${adminColors.border}`,
    [theme.breakpoints.down('sm')]: {
      padding: '12px 16px 0',
    },
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  tabs: {
    marginTop: 4,
    '& .ant-tabs-nav': {
      marginBottom: 0,
    },
    // The header's border already separates the tabs from the content.
    '& .ant-tabs-nav::before': {
      display: 'none',
    },
  },
  canvas: {
    padding: 24,
    backgroundColor: adminColors.canvas,
    borderRadius: '0 0 11px 11px',
    [theme.breakpoints.down('sm')]: {
      padding: 12,
      borderRadius: 0,
    },
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: 48,
  },
}));

export default function AdminDashboard({ onLogout }) {
  const styles = useStyles();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editGroupId, setEditGroupId] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [activeTab, setActiveTab] = useState('players');
  const [holesDirty, setHolesDirty] = useState(false);
  const [settingsDirty, setSettingsDirty] = useState(false);

  const fetchPlayers = async () => {
    try {
      const res = await fetch('/api/players/all', { credentials: 'include' });
      if (!res.ok) throw new Error('Unauthorized');
      const data = await res.json();
      setPlayers(data);
    } catch (error) {
      message.error('Failed to fetch players');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlayers();
  }, []);

  // The board and the settings form lose unsaved edits when they close, so check first.
  const confirmLeavingTab = (leave) => {
    const unsaved = (holesDirty && 'hole assignments') || (settingsDirty && 'tournament settings');

    if (!unsaved) {
      leave();
      return;
    }

    Modal.confirm({
      title: `Discard unsaved ${unsaved}?`,
      content: "Your changes haven't been saved.",
      okText: 'Discard',
      okType: 'danger',
      cancelText: 'Keep editing',
      onOk: leave,
    });
  };

  const handleLogout = () => {
    confirmLeavingTab(async () => {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
      onLogout();
    });
  };

  const handleTabChange = (key) => {
    confirmLeavingTab(() => setActiveTab(key));
  };

  const handleAddGroupSuccess = () => {
    setShowAddModal(false);
    fetchPlayers(); // Refresh the player list
  };

  const handleEditGroupSuccess = () => {
    setEditGroupId(null);
    fetchPlayers(); // Refresh the player list
  };

  // Players belonging to the group currently being edited.
  const editGroupPlayers = editGroupId
    ? players.filter(player => player.groupId === editGroupId)
    : [];

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <Title level={3} style={{ margin: 0 }}>Admin dashboard</Title>
          <Button onClick={handleLogout}>Log out</Button>
        </div>
        {/* The tabs only switch views. Their content renders in the canvas below. */}
        <Tabs activeKey={activeTab} onChange={handleTabChange} className={styles.tabs}>
          <TabPane tab="Players" key="players" />
          <TabPane tab="Hole assignments" key="holes" />
          <TabPane tab="Tournament settings" key="settings" />
        </Tabs>
      </div>

      <div className={styles.canvas}>
        {loading ? (
          <div className={styles.loading}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            {/* Stays mounted so a search survives a trip to the board. */}
            <div hidden={activeTab !== 'players'}>
              <PlayerTable
                players={players}
                onAddGroup={() => setShowAddModal(true)}
                onEditGroup={(groupId) => setEditGroupId(groupId)}
                onExport={() => setShowExportModal(true)}
              />
            </div>
            {/* Mounted only while open, so leaving the tab really discards a draft. */}
            {activeTab === 'holes' && (
              <HoleAssignmentBoard
                players={players}
                onSaved={fetchPlayers}
                onDirtyChange={setHolesDirty}
              />
            )}
            {activeTab === 'settings' && (
              <TournamentSettingsForm onDirtyChange={setSettingsDirty} />
            )}
          </>
        )}
      </div>

      <PlayerGroupModal
        mode="add"
        visible={showAddModal}
        onCancel={() => setShowAddModal(false)}
        onSuccess={handleAddGroupSuccess}
      />

      <PlayerGroupModal
        mode="edit"
        visible={!!editGroupId}
        group={editGroupPlayers}
        onCancel={() => setEditGroupId(null)}
        onSuccess={handleEditGroupSuccess}
      />

      <ExportPlayersModal
        visible={showExportModal}
        players={players}
        onCancel={() => setShowExportModal(false)}
      />
    </div>
  );
}
